import { ArrowLeft, Calendar, Clock, User, CheckCircle, Gift, Star, Smartphone, Zap, Shield, Users, MessageCircle, Heart, TrendingUp, Award, AlertCircle } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface BlogPost5Props {
    onBack: () => void;
}

export function BlogPost5({ onBack }: BlogPost5Props) {
    const { t, tObject } = useTranslation();

    const meta = tObject('blogPost5.meta') as any;
    const intro = tObject('blogPost5.intro') as any;
    const section1 = tObject('blogPost5.section1') as any;
    const section2 = tObject('blogPost5.section2') as any;
    const section3 = tObject('blogPost5.section3') as any;
    const section4 = tObject('blogPost5.section4') as any;
    const section5 = tObject('blogPost5.section5') as any;
    const section6 = tObject('blogPost5.section6') as any;
    const cta = tObject('blogPost5.cta') as any;

    return (
        <div className="min-h-screen bg-white pt-24 pb-12">
            {/* Hero Section */}
            <div className="relative h-[500px] w-full overflow-hidden mb-12">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-cyan-600 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
                    <span className="px-4 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-6 border border-white/30">
                        {meta?.badge}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold max-w-4xl mb-6 leading-tight drop-shadow-lg">
                        {meta?.title}
                    </h1>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base opacity-90">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            <span>{meta?.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>{meta?.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            <span>{meta?.readTime}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-4xl">
                <button
                    onClick={onBack}
                    className="group flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors mb-8 font-medium"
                >
                    <div className="p-2 bg-gray-100 rounded-full group-hover:bg-indigo-50 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    {meta?.backButton}
                </button>

                {/* Content Container */}
                <article className="prose prose-lg max-w-none">
                    <p className="lead text-2xl text-gray-600 mb-10 font-light leading-relaxed border-l-4 border-indigo-500 pl-6 italic">
                        {intro?.lead}
                    </p>

                    <div className="bg-indigo-50 rounded-2xl p-8 mb-12 border border-indigo-100">
                        <h3 className="text-indigo-800 font-bold text-xl mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 fill-current" />
                            {intro?.roadmapTitle}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-3">
                            <ul className="list-none space-y-2 m-0">
                                {intro?.roadmap?.slice(0, 3).map((item: string, i: number) => (
                                    <li key={i} className="flex items-center gap-2 text-gray-700">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <ul className="list-none space-y-2 m-0">
                                {intro?.roadmap?.slice(3).map((item: string, i: number) => (
                                    <li key={i} className="flex items-center gap-2 text-gray-700">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Seção 1 */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-indigo-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-indigo-600 text-white rounded-xl text-2xl font-bold">1</span>
                            {section1?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section1?.intro}</p>

                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                            <div className="bg-red-50 p-6 rounded-xl border border-red-100 hover:bg-red-100 transition-colors">
                                <h4 className="font-bold text-red-700 mb-3 flex items-center gap-2 text-lg">
                                    <AlertCircle className="w-5 h-5" />
                                    {section1?.traditional?.title}
                                </h4>
                                <ul className="text-base text-gray-700 space-y-3 list-disc pl-5 leading-relaxed">
                                    <li><strong>{section1?.traditional?.loop?.split(':')[0]}:</strong> {section1?.traditional?.loop?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.traditional?.logistics?.split(':')[0]}:</strong> {section1?.traditional?.logistics?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.traditional?.geographic?.split(':')[0]}:</strong> {section1?.traditional?.geographic?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.traditional?.secrecy?.split(':')[0]}:</strong> {section1?.traditional?.secrecy?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.traditional?.guessing?.split(':')[0]}:</strong> {section1?.traditional?.guessing?.split(':').slice(1).join(':')}</li>
                                </ul>
                            </div>
                            <div className="bg-green-50 p-6 rounded-xl border border-green-100 hover:bg-green-100 transition-colors">
                                <h4 className="font-bold text-green-700 mb-3 flex items-center gap-2 text-lg">
                                    <CheckCircle className="w-5 h-5" />
                                    {section1?.modern?.title}
                                </h4>
                                <ul className="text-base text-gray-700 space-y-3 list-disc pl-5 leading-relaxed">
                                    <li><strong>{section1?.modern?.algorithms?.split(':')[0]}:</strong> {section1?.modern?.algorithms?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.modern?.connectivity?.split(':')[0]}:</strong> {section1?.modern?.connectivity?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.modern?.intelligence?.split(':')[0]}:</strong> {section1?.modern?.intelligence?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.modern?.gamification?.split(':')[0]}:</strong> {section1?.modern?.gamification?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{section1?.modern?.automation?.split(':')[0]}:</strong> {section1?.modern?.automation?.split(':').slice(1).join(':')}</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Seção 2 */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-cyan-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-cyan-600 text-white rounded-xl text-2xl font-bold">2</span>
                            {section2?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section2?.intro}</p>

                        <div className="space-y-8">
                            <div className="flex gap-6 items-start bg-gray-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Smartphone className="w-7 h-7 text-blue-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-xl mb-3 text-gray-900">{section2?.features?.interface?.title}</h4>
                                    <p className="text-gray-600 text-base leading-relaxed">{section2?.features?.interface?.description}</p>
                                </div>
                            </div>

                            <div className="flex gap-6 items-start bg-gray-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <MessageCircle className="w-7 h-7 text-purple-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-xl mb-3 text-gray-900">{section2?.features?.chat?.title}</h4>
                                    <p className="text-gray-600 text-base leading-relaxed">{section2?.features?.chat?.description}</p>
                                </div>
                            </div>

                            <div className="flex gap-6 items-start bg-gray-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <Gift className="w-7 h-7 text-green-600" />
                                </div>
                                <div>
                                    <h4 className="font-bold text-xl mb-3 text-gray-900">{section2?.features?.wishlist?.title}</h4>
                                    <p className="text-gray-600 text-base leading-relaxed">{section2?.features?.wishlist?.description}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Seção 3 - Comparativo */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-yellow-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-yellow-500 text-white rounded-xl text-2xl font-bold">3</span>
                            {section3?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section3?.intro}</p>

                        <div className="overflow-x-auto mb-8 shadow-xl rounded-xl border border-gray-200">
                            <table className="w-full border-collapse bg-white">
                                <thead className="bg-gray-800 text-white">
                                    <tr>
                                        {section3?.table?.headers?.map((header: string, i: number) => (
                                            <th key={i} className={`p-4 text-center font-bold text-lg ${i === 0 ? 'text-left rounded-tl-xl' : ''} ${i === 4 ? 'rounded-tr-xl' : ''}`}>
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr className="hover:bg-indigo-50 transition-colors bg-indigo-50/50">
                                        <td className="p-4 font-extrabold text-indigo-700 flex items-center gap-2">
                                            <Award className="w-5 h-5" /> {section3?.table?.mysteryGift?.name}
                                        </td>
                                        <td className="p-4 text-center text-green-600 font-bold">{section3?.table?.mysteryGift?.free}</td>
                                        <td className="p-4 text-center text-green-600 font-bold">{section3?.table?.mysteryGift?.chat}</td>
                                        <td className="p-4 text-center text-green-600 font-bold">{section3?.table?.mysteryGift?.wishlist}</td>
                                        <td className="p-4 text-center text-purple-600 font-bold">{section3?.table?.mysteryGift?.ai}</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50">
                                        <td className="p-4 font-medium text-gray-800">{section3?.table?.amigoSecretoOnline?.name}</td>
                                        <td className="p-4 text-center text-green-600">{section3?.table?.amigoSecretoOnline?.free}</td>
                                        <td className="p-4 text-center text-red-500">{section3?.table?.amigoSecretoOnline?.chat}</td>
                                        <td className="p-4 text-center text-gray-600">{section3?.table?.amigoSecretoOnline?.wishlist}</td>
                                        <td className="p-4 text-center text-red-500">{section3?.table?.amigoSecretoOnline?.ai}</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50">
                                        <td className="p-4 font-medium text-gray-800">{section3?.table?.sorteioFacil?.name}</td>
                                        <td className="p-4 text-center text-yellow-600">{section3?.table?.sorteioFacil?.free}</td>
                                        <td className="p-4 text-center text-green-600">{section3?.table?.sorteioFacil?.chat}</td>
                                        <td className="p-4 text-center text-red-500">{section3?.table?.sorteioFacil?.wishlist}</td>
                                        <td className="p-4 text-center text-red-500">{section3?.table?.sorteioFacil?.ai}</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50">
                                        <td className="p-4 font-medium text-gray-800">{section3?.table?.drawNames?.name}</td>
                                        <td className="p-4 text-center text-green-600">{section3?.table?.drawNames?.free}</td>
                                        <td className="p-4 text-center text-red-500">{section3?.table?.drawNames?.chat}</td>
                                        <td className="p-4 text-center text-gray-600">{section3?.table?.drawNames?.wishlist}</td>
                                        <td className="p-4 text-center text-red-500">{section3?.table?.drawNames?.ai}</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50">
                                        <td className="p-4 font-medium text-gray-800">{section3?.table?.elfster?.name}</td>
                                        <td className="p-4 text-center text-green-600">{section3?.table?.elfster?.free}</td>
                                        <td className="p-4 text-center text-yellow-600">{section3?.table?.elfster?.chat}</td>
                                        <td className="p-4 text-center text-green-600">{section3?.table?.elfster?.wishlist}</td>
                                        <td className="p-4 text-center text-red-500">{section3?.table?.elfster?.ai}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Seção 4 - Destaque Mystery Gift */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-purple-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-purple-600 text-white rounded-xl text-2xl font-bold">4</span>
                            {section4?.title}
                        </h2>

                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 mb-8 border-2 border-indigo-200 shadow-lg">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="bg-indigo-600 p-2 rounded-lg">
                                    <Award className="w-8 h-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-bold text-indigo-900">{section4?.subtitle}</h3>
                            </div>
                            <p className="text-gray-700 mb-8 leading-relaxed text-lg">{section4?.intro}</p>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm">
                                    <Zap className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                                    <div>
                                        <strong className="block text-indigo-900 text-lg mb-1">{section4?.features?.ai?.title}</strong>
                                        <p className="text-sm text-gray-600">{section4?.features?.ai?.description}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm">
                                    <Shield className="w-8 h-8 text-green-500 flex-shrink-0 mt-1" />
                                    <div>
                                        <strong className="block text-indigo-900 text-lg mb-1">{section4?.features?.anonymity?.title}</strong>
                                        <p className="text-sm text-gray-600">{section4?.features?.anonymity?.description}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm">
                                    <Users className="w-8 h-8 text-blue-500 flex-shrink-0 mt-1" />
                                    <div>
                                        <strong className="block text-indigo-900 text-lg mb-1">{section4?.features?.scalability?.title}</strong>
                                        <p className="text-sm text-gray-600">{section4?.features?.scalability?.description}</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4 bg-white p-4 rounded-xl shadow-sm">
                                    <TrendingUp className="w-8 h-8 text-purple-500 flex-shrink-0 mt-1" />
                                    <div>
                                        <strong className="block text-indigo-900 text-lg mb-1">{section4?.features?.evolution?.title}</strong>
                                        <p className="text-sm text-gray-600">{section4?.features?.evolution?.description}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Seção 5 - Segurança */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-red-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-red-600 text-white rounded-xl text-2xl font-bold">5</span>
                            {section5?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section5?.intro}</p>

                        <div className="bg-yellow-50 border-l-8 border-yellow-500 p-8 mb-6 rounded-r-xl">
                            <h4 className="font-bold text-yellow-900 mb-4 text-xl flex items-center gap-2">
                                <Shield className="w-6 h-6" /> {section5?.checklistTitle}
                            </h4>
                            <ul className="list-disc pl-5 text-gray-700 space-y-4 text-lg">
                                <li>
                                    <strong>{section5?.checklist?.privacy?.split(':')[0]}:</strong> {section5?.checklist?.privacy?.split(':').slice(1).join(':')}
                                </li>
                                <li>
                                    <strong>{section5?.checklist?.https?.split(':')[0]}:</strong> {section5?.checklist?.https?.split(':').slice(1).join(':')}
                                </li>
                                <li>
                                    <strong>{section5?.checklist?.deletion?.split(':')[0]}:</strong> {section5?.checklist?.deletion?.split(':').slice(1).join(':')}
                                </li>
                                <li>
                                    <strong>{section5?.checklist?.authentication?.split(':')[0]}:</strong> {section5?.checklist?.authentication?.split(':').slice(1).join(':')}
                                </li>
                            </ul>
                        </div>
                    </section>

                    {/* Seção 6 - Dicas */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-green-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-green-600 text-white rounded-xl text-2xl font-bold">6</span>
                            {section6?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{section6?.intro}</p>

                        <div className="space-y-6">
                            <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:border-indigo-200 transition-colors shadow-sm">
                                <h4 className="font-bold text-xl mb-3 text-gray-900 flex items-center gap-2">
                                    <span className="bg-indigo-100 text-indigo-700 w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                                    {section6?.tips?.rules?.title}
                                </h4>
                                <p className="text-gray-600 text-base leading-relaxed">{section6?.tips?.rules?.description}</p>
                            </div>
                            <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:border-indigo-200 transition-colors shadow-sm">
                                <h4 className="font-bold text-xl mb-3 text-gray-900 flex items-center gap-2">
                                    <span className="bg-indigo-100 text-indigo-700 w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                                    {section6?.tips?.wishlist?.title}
                                </h4>
                                <p className="text-gray-600 text-base leading-relaxed">{section6?.tips?.wishlist?.description}</p>
                            </div>
                            <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 hover:border-indigo-200 transition-colors shadow-sm">
                                <h4 className="font-bold text-xl mb-3 text-gray-900 flex items-center gap-2">
                                    <span className="bg-indigo-100 text-indigo-700 w-8 h-8 rounded-full flex items-center justify-center text-sm">3</span>
                                    {section6?.tips?.chat?.title}
                                </h4>
                                <p className="text-gray-600 text-base leading-relaxed">{section6?.tips?.chat?.description}</p>
                            </div>
                        </div>
                    </section>

                    <hr className="my-12 border-gray-200" />

                    {/* CTA Final */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-8 md:p-16 text-white text-center shadow-2xl relative overflow-hidden transform transition-all hover:scale-[1.01]">
                        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                        <Smartphone className="w-24 h-24 mx-auto mb-8 text-cyan-200 animate-bounce drop-shadow-md" />
                        <h2 className="text-3xl md:text-5xl font-bold mb-8 relative z-10 leading-tight">{cta?.title}</h2>
                        <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10 font-light">
                            {cta?.description}
                        </p>
                        <button
                            onClick={onBack}
                            className="bg-white text-indigo-600 px-12 py-6 rounded-full font-bold text-xl hover:bg-gray-50 hover:shadow-2xl hover:-translate-y-1 transition-all shadow-xl relative z-10 flex items-center gap-4 mx-auto group"
                        >
                            <Gift className="w-7 h-7 group-hover:rotate-12 transition-transform" />
                            {cta?.button}
                        </button>
                    </div>
                </article>
            </div>
        </div>
    );
}